import { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Button,
  Grid,
  Alert,
  Divider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { Add, DeleteOutline, AccessTime, Percent, AccountBalance, CardGiftcard } from '@mui/icons-material';

export type BillingType = 'subscription' | 'service_fee' | 'revenue_share';
export type ServiceFeeMode = 'percentage' | 'tiered';

export interface ServiceFeeTier {
  min: number | '';
  max: number | '';
  fee: number | '';
}

export interface BillingFormValue {
  type: BillingType;
  starts_at: string;
  ends_at: string;
  is_trial: boolean;
  subscription_amount: number | '';
  subscription_currency: string;
  service_fee_mode: ServiceFeeMode;
  service_fee_percentage: number | '';
  service_fee_tiers: ServiceFeeTier[];
  revenue_share_provider_percentage: number | '';
  revenue_share_county_percentage: number | '';
  notes: string;
}

export const emptyBilling = (): BillingFormValue => ({
  type: 'subscription',
  starts_at: new Date().toISOString().slice(0, 10),
  ends_at: '',
  is_trial: false,
  subscription_amount: '',
  subscription_currency: 'KES',
  service_fee_mode: 'percentage',
  service_fee_percentage: '',
  service_fee_tiers: [{ min: 1, max: 1000, fee: '' }],
  revenue_share_provider_percentage: 20,
  revenue_share_county_percentage: 80,
  notes: '',
});

interface Props {
  value: BillingFormValue;
  onChange: (next: BillingFormValue) => void;
}

export function BillingConfigForm({ value, onChange }: Props) {
  const set = <K extends keyof BillingFormValue>(key: K, v: BillingFormValue[K]) =>
    onChange({ ...value, [key]: v });

  useEffect(() => {
    if (value.type === 'revenue_share') {
      const provider = Number(value.revenue_share_provider_percentage || 0);
      const expectedCounty = Math.max(0, Math.min(100, 100 - provider));
      if (Number(value.revenue_share_county_percentage) !== expectedCounty) {
        set('revenue_share_county_percentage', expectedCounty);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.revenue_share_provider_percentage, value.type]);

  const updateTier = (idx: number, patch: Partial<ServiceFeeTier>) => {
    const tiers = [...value.service_fee_tiers];
    tiers[idx] = { ...tiers[idx], ...patch };
    set('service_fee_tiers', tiers);
  };

  const addTier = () => {
    const last = value.service_fee_tiers[value.service_fee_tiers.length - 1];
    const nextMin = last && typeof last.max === 'number' ? last.max + 1 : 1;
    set('service_fee_tiers', [...value.service_fee_tiers, { min: nextMin, max: '', fee: '' }]);
  };

  const removeTier = (idx: number) => {
    if (value.service_fee_tiers.length <= 1) return;
    set('service_fee_tiers', value.service_fee_tiers.filter((_, i) => i !== idx));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary" fontWeight={700}>
          Billing Type
        </Typography>
        <Tabs
          value={value.type}
          onChange={(_, v) => set('type', v as BillingType)}
          sx={{ mb: 3, mt: 1 }}
        >
          <Tab icon={<AccessTime />} iconPosition="start" value="subscription" label="Subscription" />
          <Tab icon={<Percent />} iconPosition="start" value="service_fee" label="Service Fee" />
          <Tab icon={<AccountBalance />} iconPosition="start" value="revenue_share" label="Revenue Share" />
        </Tabs>

        {value.type === 'subscription' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              County is granted access for a fixed period. After the end date the system blocks payments
              and the county admin sees an &ldquo;expired&rdquo; screen until renewed.
            </Alert>

            <Card
              variant="outlined"
              sx={{
                mb: 2,
                bgcolor: value.is_trial ? 'success.bg' : 'background.default',
                borderColor: value.is_trial ? 'success.main' : undefined,
                borderLeft: value.is_trial ? (theme) => `4px solid ${theme.palette.success.main}` : undefined,
              }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={value.is_trial}
                      onChange={(e) => {
                        const on = e.target.checked;
                        if (on) {
                          // Trials are free — zero out and lock the amount.
                          onChange({ ...value, is_trial: true, subscription_amount: 0 });
                        } else {
                          onChange({ ...value, is_trial: false, subscription_amount: '' });
                        }
                      }}
                      color="success"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CardGiftcard fontSize="small" color={value.is_trial ? 'success' : 'disabled'} />
                      <Typography variant="body2" fontWeight={600}>Grant as free trial</Typography>
                      {value.is_trial && <Chip size="small" color="success" label="Trial" sx={{ fontWeight: 700 }} />}
                    </Stack>
                  }
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 6 }}>
                  {value.is_trial
                    ? 'County gets full access until the end date at no cost. You can extend the trial later if needed.'
                    : 'Toggle on to give this county a free trial up to a specific end date.'}
                </Typography>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Starts"
                  InputLabelProps={{ shrink: true }}
                  value={value.starts_at}
                  onChange={(e) => set('starts_at', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={value.is_trial ? 'Trial ends' : 'Ends'}
                  InputLabelProps={{ shrink: true }}
                  value={value.ends_at}
                  onChange={(e) => set('ends_at', e.target.value)}
                  required
                />
              </Grid>
              {!value.is_trial && (
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Subscription amount"
                    value={value.subscription_amount}
                    onChange={(e) =>
                      set('subscription_amount', e.target.value === '' ? '' : Number(e.target.value))
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{value.subscription_currency}</InputAdornment>,
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {value.type === 'service_fee' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              A convenience fee is added on top of every transaction. Citizens see it itemized on the
              payment summary.
            </Alert>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={value.service_fee_mode}
              onChange={(_, v) => v && set('service_fee_mode', v as ServiceFeeMode)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="percentage">Percentage</ToggleButton>
              <ToggleButton value="tiered">Tiered</ToggleButton>
            </ToggleButtonGroup>

            {value.service_fee_mode === 'percentage' && (
              <TextField
                fullWidth
                type="number"
                label="Percentage of transaction"
                value={value.service_fee_percentage}
                onChange={(e) =>
                  set('service_fee_percentage', e.target.value === '' ? '' : Number(e.target.value))
                }
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="e.g. 2.5 means the citizen pays 102.5% of the service amount"
              />
            )}

            {value.service_fee_mode === 'tiered' && (
              <Box>
                <Stack spacing={1}>
                  {value.service_fee_tiers.map((tier, idx) => (
                    <Grid container spacing={1} key={idx} alignItems="center">
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="From (KES)"
                          value={tier.min}
                          onChange={(e) =>
                            updateTier(idx, { min: e.target.value === '' ? '' : Number(e.target.value) })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="To (KES, blank = ∞)"
                          value={tier.max}
                          onChange={(e) =>
                            updateTier(idx, { max: e.target.value === '' ? '' : Number(e.target.value) })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Fee (KES)"
                          value={tier.fee}
                          onChange={(e) =>
                            updateTier(idx, { fee: e.target.value === '' ? '' : Number(e.target.value) })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 2 }}>
                        <IconButton onClick={() => removeTier(idx)} disabled={value.service_fee_tiers.length <= 1}>
                          <DeleteOutline />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
                <Button startIcon={<Add />} onClick={addTier} sx={{ mt: 1 }}>
                  Add tier
                </Button>
              </Box>
            )}
          </Box>
        )}

        {value.type === 'revenue_share' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Each completed transaction is split logically. The county receives their share to their
              paybill; the provider&rsquo;s share is settled in batches via a payout cycle.
            </Alert>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Provider (superadmin) share"
                  value={value.revenue_share_provider_percentage}
                  onChange={(e) =>
                    set(
                      'revenue_share_provider_percentage',
                      e.target.value === '' ? '' : Number(e.target.value),
                    )
                  }
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="County share"
                  value={value.revenue_share_county_percentage}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    readOnly: true,
                  }}
                  helperText="Auto-calculated as 100% minus provider share"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Internal notes (optional)"
          value={value.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </CardContent>
    </Card>
  );
}

export function validateBilling(v: BillingFormValue): string | null {
  if (v.type === 'subscription') {
    if (!v.ends_at) return v.is_trial ? 'Trial end date is required' : 'Subscription end date is required';
    if (!v.is_trial && (v.subscription_amount === '' || Number(v.subscription_amount) < 0))
      return 'Subscription amount must be 0 or greater';
  }
  if (v.type === 'service_fee') {
    if (v.service_fee_mode === 'percentage') {
      if (v.service_fee_percentage === '' || Number(v.service_fee_percentage) < 0)
        return 'Service fee percentage is required';
    } else {
      if (!v.service_fee_tiers.length) return 'At least one tier is required';
      for (const t of v.service_fee_tiers) {
        if (t.min === '' || t.fee === '') return 'Each tier needs a min and a fee';
      }
    }
  }
  if (v.type === 'revenue_share') {
    const p = Number(v.revenue_share_provider_percentage);
    if (Number.isNaN(p) || p < 0 || p > 100) return 'Provider share must be between 0 and 100';
  }
  return null;
}

/**
 * Strips empty values and normalises the form payload into something the backend accepts.
 */
export function serializeBilling(v: BillingFormValue): Record<string, unknown> {
  const base: Record<string, unknown> = { type: v.type, notes: v.notes || null };

  if (v.type === 'subscription') {
    base.starts_at = v.starts_at || null;
    base.ends_at = v.ends_at;
    base.is_trial = !!v.is_trial;
    base.subscription_amount = v.is_trial ? 0 : Number(v.subscription_amount);
    base.subscription_currency = v.subscription_currency || 'KES';
  }
  if (v.type === 'service_fee') {
    base.service_fee_mode = v.service_fee_mode;
    if (v.service_fee_mode === 'percentage') {
      base.service_fee_percentage = Number(v.service_fee_percentage);
    } else {
      base.service_fee_tiers = v.service_fee_tiers.map((t) => ({
        min: Number(t.min),
        max: t.max === '' ? null : Number(t.max),
        fee: Number(t.fee),
      }));
    }
  }
  if (v.type === 'revenue_share') {
    base.revenue_share_provider_percentage = Number(v.revenue_share_provider_percentage);
    base.revenue_share_county_percentage = Number(v.revenue_share_county_percentage);
  }
  return base;
}
